import React, { ReactNode, useEffect, useState } from 'react';
import { ExpandCard } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Card, Collapse, SxProps, Typography } from '@mui/material';

const StyledCard = (expanded, isStatic, variant) => ({
  cursor: expanded ? undefined : 'pointer',
  mt: 1,
  ...(expanded
    ? {}
    : {
        '&:hover': {
          ...(variant === 'list' && { boxShadow: '0px 2px 20px 0px rgba(18, 18, 27, 0.15)' }),
        },
        '&:not(:hover)': {
          boxShadow: 'none',
        },
      }),
  ...(variant === 'list' && {
    flexShrink: 0,
    borderRadius: 3,
    border: '1px solid',
    borderColor: theme => theme.palette.colors.neutrals[350],
    mt: 0,
    p: 0,
  }),
  '.card-header': {
    ...flex.justifyBetweenCenter,
    cursor: isStatic ? undefined : 'pointer',
  },
  '> .MuiCollapse-root > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner': {
    ...flex.col,
    pt: 3,
    gap: '10px',
  },
});

interface AvFormCardProps {
  title: string | ReactNode;
  description?: string | ReactNode;
  open?: boolean;
  isStatic?: boolean;
  children: ReactNode;
  fullScreen?: boolean;
  toggleFullScreen?: () => void;
  customGlobalActions?: ReactNode[];
  sx?: SxProps;
  variant?: 'normal' | 'list';
  showCustomGlobalOptionsWhenCollapsed?: boolean;
  onCloseProps?: ReactNode;
  headerSx?: SxProps;
  titleRightAdornment?: ReactNode;
}

export const AvFormCard: React.FC<AvFormCardProps> = ({
  title,
  description,
  open = true,
  isStatic,
  children,
  fullScreen,
  toggleFullScreen,
  customGlobalActions,
  sx,
  variant = 'normal',
  showCustomGlobalOptionsWhenCollapsed = false,
  onCloseProps = null,
  headerSx,
  titleRightAdornment,
}) => {
  const [expanded, setExpanded] = useState(open || isStatic);

  useEffect(() => {
    setExpanded(open);
  }, [open]);

  const handleExpandClick = () => setExpanded(!expanded);
  return (
    <Card raised sx={{ ...StyledCard(expanded, isStatic, variant), ...sx }} onClick={expanded || isStatic ? undefined : handleExpandClick}>
      <Box
        className="card-header"
        onClick={isStatic ? undefined : handleExpandClick}
        sx={{
          ...(variant === 'list' && {
            boxSizing: 'content-box',
            height: 24,
            py: 2.5,
            px: 3,
            ...(expanded && { borderBottom: '1px solid', borderBottomColor: theme => theme.palette.colors.neutrals[350] }),
          }),
          ...headerSx,
        }}>
        <Box sx={{ ...flex[onCloseProps ? 'itemsCenter' : 'justifyBetweenCenter'], width: 'calc(100% - 35px)' }}>
          <Typography variant="h5" sx={{ flexShrink: 1 }}>
            {title}
          </Typography>
          {!expanded && onCloseProps}
          <Box
            sx={{
              ...flex.itemsCenter,
              mr: 2,
              gap: 1,
              transition: 'opacity 0.1s',
              opacity: expanded || showCustomGlobalOptionsWhenCollapsed ? 1 : 0,
            }}
            onClick={e => e.stopPropagation()}>
            {customGlobalActions?.map((action, index) => (
              <React.Fragment key={`action-${index}`}>{action}</React.Fragment>
            ))}
          </Box>
        </Box>
        {!!titleRightAdornment && titleRightAdornment}
        {!isStatic && <ExpandCard expand={expanded} sx={{ height: 18 }} fullScreen={fullScreen} toggleFullScreen={toggleFullScreen} />}
      </Box>
      {description && <Box sx={{ color: ({ palette }) => palette.colors.neutrals[700], mt: '6px' }}>{description}</Box>}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {children}
      </Collapse>
    </Card>
  );
};
